import "../assets/qreader.css";
import { useState } from "react";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../stores/useDialogStore";
import { useForm } from "react-hook-form";

// Option configuration for DRY logic
const OPTION_CONFIG = {
  white: {
    finisher: 1,
    rt_lct: 1,
    blue: 0,
    green: 0,
    cabinet: 0,
    no_df: 0,

    recycled_date: null,
    spare_parts: null,
  },
  green: {
    green: 1,
    blue: 0,
    finisher: 0,
    rt_lct: 0,
    cabinet: 0,
    no_df: 0,

    recycled_date: null,
    spare_parts: null,
  },
  blue: {
    blue: 1,
    green: 0,
    finisher: 0,
    rt_lct: 0,
    cabinet: 0,
    no_df: 0,

    recycled_date: null,
    spare_parts: null,
  },
  spare_parts: (date) => ({ spare_parts: date, recycled_date: null }),
  recycled_date: (date) => ({ recycled_date: date, spare_parts: null }),
};

export default function MoveDevices() {
  const { closeDialog } = useDialogStore();
  const [textareaValue, setTextareaValue] = useState("");
  const queryClient = useQueryClient();

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      green: 0,
      blue: 0,
      finisher: 0,
      rt_lct: 0,
      cabinet: 0,
      no_df: 0,
      recycled_date: null,
      spare_parts: null,
    },
  });

  // Parse unique IDs from textarea
  const uniqueIdsArray = textareaValue
    .split("\n")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);

  // Unified option click handler
  const handleOptionClick = (option) => {
    if (option === "spare_parts" || option === "recycled_date") {
      const today = new Date().toISOString().split("T")[0];
      const config = OPTION_CONFIG[option](today);
      Object.entries(config).forEach(([key, value]) => setValue(key, value));
    } else {
      const config = OPTION_CONFIG[option];
      Object.entries(config).forEach(([key, value]) => setValue(key, value));
    }
  };

  // Helper to determine if a button is active
  const isActive = (option) => {
    if (option === "white")
      return watch("finisher") == 1 && watch("rt_lct") == 1;
    if (option === "green") return watch("green") == 1;
    if (option === "blue") return watch("blue") == 1;
    if (option === "spare_parts") return !!watch("spare_parts");
    if (option === "recycled_date") return !!watch("recycled_date");
    return false;
  };

  const moveDevicesMutation = useMutation({
    mutationFn: async (deviceOptions) => {
      return axiosInstance.post(
        window.REACT_APP_API_ENDPOINT + "/device-options/multiple-update",
        {
          device_unique_ids: uniqueIdsArray,
          device_options: deviceOptions,
        }
      );
    },
    onSuccess: (response) => {
      if (response) {
        queryClient.invalidateQueries({ queryKey: ["reserved-devices"] });
        queryClient.invalidateQueries({ queryKey: ["devices"] });
        queryClient.invalidateQueries({ queryKey: ["sparePartsDevices"] });
        queryClient.invalidateQueries({ queryKey: ["recycledDevices"] });
        queryClient.invalidateQueries({ queryKey: ["sold-devices"] });
        toast.success("Devices moved successfully");
        closeDialog();
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Device not found");
    },
  });
  const onSubmit = (data) => {
    if (data.recycled_date || data.spare_parts) {
      ["green", "blue", "finisher", "rt_lct", "cabinet", "no_df"].forEach(
        (key) => delete data[key]
      );
    }
    console.log(data);

    moveDevicesMutation.mutate(data);
  };

  // Register all form fields as hidden inputs
  const hiddenFields = [
    "green",
    "blue",
    "recycled_date",
    "finisher",
    "rt_lct",
    "cabinet",
    "no_df",
    "spare_parts",
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {hiddenFields.map((field) => (
        <input key={field} type="hidden" {...register(field)} />
      ))}
      <div className="mb-2 text-sm text-gray-700">
        Enter Unique IDs (one per line)
      </div>
      <textarea
        className="w-full border rounded-md p-2 min-h-[100px] text-gray-900 placeholder:text-gray-400 text-sm"
        placeholder="Type or paste and press Enter..."
        value={textareaValue}
        onChange={(e) => setTextareaValue(e.target.value)}
        onPaste={(e) => {
          e.preventDefault();
          const pastedData = e.clipboardData.getData("Text");
          setTextareaValue((prev) =>
            prev
              ? prev + "\n" + pastedData.replace(/\r/g, "")
              : pastedData.replace(/\r/g, "")
          );
        }}
        autoFocus
      />
      <div className="text-xs text-gray-500 mb-3">
        Paste a list of values or press Enter after each entry
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          aria-pressed={isActive("white")}
          onClick={() => handleOptionClick("white")}
          className={`bg-gray-50 hover:border-gray-500 border text-gray-800 px-3 py-1 rounded text-xs font-semibold ${
            isActive("white") ? "bg-neutral-500 text-white" : ""
          }`}
        >
          White
        </button>

        <button
          type="button"
          aria-pressed={isActive("blue")}
          onClick={() => handleOptionClick("blue")}
          className={`bg-blue-50 hover:border-blue-400 border text-blue-900 px-3 py-1 rounded text-xs font-semibold ${
            isActive("blue") ? "bg-blue-500 text-white" : ""
          }`}
        >
          Blue
        </button>
        <button
          type="button"
          aria-pressed={isActive("green")}
          onClick={() => handleOptionClick("green")}
          className={`bg-green-50 hover:border-green-400 border text-green-900 px-3 py-1 rounded text-xs font-semibold ${
            isActive("green") ? "bg-green-500 text-white" : ""
          }`}
        >
          Green
        </button>
        <button
          type="button"
          aria-pressed={isActive("spare_parts")}
          onClick={() => handleOptionClick("spare_parts")}
          className={`bg-yellow-50 hover:border-yellow-500 border text-yellow-800 px-3 py-1 rounded text-xs font-semibold ${
            isActive("spare_parts") ? "bg-yellow-500 text-white" : ""
          }`}
        >
          Spare Parts
        </button>
        <button
          type="button"
          aria-pressed={isActive("recycled_date")}
          onClick={() => handleOptionClick("recycled_date")}
          className={`bg-indigo-50 hover:border-indigo-500 border text-indigo-800 px-3 py-1 rounded text-xs font-semibold ${
            isActive("recycled_date") ? "bg-indigo-500 text-white" : ""
          }`}
        >
          Recycled
        </button>
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm ${
            !(
              isActive("white") ||
              isActive("blue") ||
              isActive("green") ||
              isActive("spare_parts") ||
              isActive("recycled_date")
            )
              ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
              : ""
          }`}
          disabled={
            uniqueIdsArray.length === 0 ||
            moveDevicesMutation.isLoading ||
            !(
              isActive("white") ||
              isActive("blue") ||
              isActive("green") ||
              isActive("spare_parts") ||
              isActive("recycled_date")
            )
          }
        >
          {moveDevicesMutation.isLoading ? "Moving..." : "Move Devices"}
        </button>
      </div>
    </form>
  );
}
